import React, { useState, useEffect, useRef } from "react";
import "./WorkloadMemberDetail.css";
import ProfileIcon from "../../Assets/png/user-icon.png";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, Clock, Layers } from "lucide-react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchUserWorkloadData } from "./Workloadapi";
import AssignTicketModal from "./AssignTicketModal";

const WorkloadMemberDetail = ({ member, onBack, onSuccess }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("All");
  const [selectedProject, setSelectedProject] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All Priority");
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [selectedMonthDate, setSelectedMonthDate] = useState(moment());
  const [activeDate, setActiveDate] = useState(moment().format("YYYY-MM-DD"));
  const [monthDates, setMonthDates] = useState([]);
  const [selectedTicketToAssign, setSelectedTicketToAssign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workloadData, setWorkloadData] = useState(null);
  const scrollRef = useRef(null);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const daysInMonth = selectedMonthDate.daysInMonth();
    const dates = Array.from({ length: daysInMonth }, (_, i) => {
      return moment(selectedMonthDate).date(i + 1).format("YYYY-MM-DD");
    });
    setMonthDates(dates);

    // If current activeDate is not in the new month, set it to the first of the month
    if (!dates.includes(activeDate)) {
      setActiveDate(dates[0]);
    }
  }, [selectedMonthDate]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      const handleWheelNative = (e) => {
        if (e.deltaY === 0) return;
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      };
      el.addEventListener('wheel', handleWheelNative, { passive: false });
      return () => el.removeEventListener('wheel', handleWheelNative);
    }
  }, []);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        const response = await fetchUserWorkloadData(member.id, activeDate);

        if (response && response.data && response.data.success) {
          setWorkloadData(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch member workload details", error);
      } finally {
        setLoading(false);
      }
    };

    if (member && activeDate) {
      fetchUserDetails();
    }
  }, [member, activeDate, refreshTrigger]);

  const employee = workloadData?.employee || member;
  const projectHours = workloadData?.project_hours || [];
  const tickets = workloadData?.tickets || [];
  const summary = workloadData?.summary || { total_tickets: 0, total_hours: '00:00 hrs' };

  // Base filtering by Project AND Priority
  const baseFiltered = tickets.filter(t => {
    const matchesProject = selectedProject === "All" || t.project === selectedProject;
    const matchesPriority = priorityFilter === "All Priority" || t.priority?.toLowerCase() === priorityFilter.toLowerCase();
    return matchesProject && matchesPriority;
  });

  const tabCounts = {
    All: baseFiltered.length,
    Open: baseFiltered.filter(t => t.status?.toLowerCase() === "open").length,
    "In Progress": baseFiltered.filter(t => t.status?.toLowerCase() === "in progress").length,
    Hold: baseFiltered.filter(t => t.status?.toLowerCase() === "hold").length,
  };

  // Final filtered list based on Tab
  const filteredTickets = baseFiltered.filter(ticket => {
    if (activeTab === "All") return true;
    return ticket.status?.toLowerCase() === activeTab.toLowerCase();
  });

  return (
    <div className="workload-member-detail-page">
      {/* Header */}
      <div className="member-detail-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>
            <ArrowLeft size={20} />
          </button>
          <div className="header-profile">
            <img src={employee?.profile_image || ProfileIcon} alt="Profile" className="header-avatar" />
            <div className="header-info">
              <h2 className="name">{employee?.name || "Member Name"}</h2>
              <p className="role">{employee?.designation || "-"}</p>
            </div>
          </div>
        </div>
        <div className="header-right">
          <span className="reporter-label">Reporter :</span>
          {/* <img src={`https://ui-avatars.com/api/?name=${employee?.reporting_person_name || 'Reporter'}&background=random`} alt="Reporter" className="reporter-avatar" /> */}
          <span className="reporter-name">{employee?.reporting_person_name || "-"}</span>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="date-nav-row">
        <div
          className="dates-carousel"
          ref={scrollRef}
        >
          {monthDates.map((dateStr, idx) => (
            <button
              key={idx}
              className={`date-pill ${activeDate === dateStr ? "active" : ""}`}
              onClick={() => setActiveDate(dateStr)}
            >
              {moment(dateStr).format("MMM D")}
            </button>
          ))}
        </div>
        <div className="month-selector">
          <button className="month-arrow" onClick={() => setSelectedMonthDate(prev => moment(prev).subtract(1, 'month'))}>
            <ChevronLeft size={16} />
          </button>
          <span className="month-text">
            <DatePicker
              selected={selectedMonthDate.toDate()}
              onChange={(date) => setSelectedMonthDate(moment(date))}
              customInput={
                <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {selectedMonthDate.format("MMMM YYYY")}
                  <ChevronDown size={16} color="#8790A1" />
                </span>
              }
              dateFormat="MMMM yyyy"
              showMonthYearPicker
            />
          </span>
          <button className="month-arrow" onClick={() => setSelectedMonthDate(prev => moment(prev).add(1, 'month'))}>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="detail-main-content">
        {loading ? (
          <div className="loading-container" style={{ padding: '100px', textAlign: 'center', width: '100%' }}>Loading details...</div>
        ) : (
          <>
            {/* Left Column */}
            <div className="detail-left-col">
              <div className="summary-card">
                <span className="stat-date">{moment(activeDate).format("D MMMM YYYY")}</span>
                <span className="stat-dot">•</span>
                <span className="stat-tickets">{summary.total_tickets} tickets</span>
                <span className="stat-dot">•</span>
                <span className="stat-hours">{summary.total_hours}</span>
              </div>

              <div className="sidebar-card">
                <h4 className="sidebar-title">Project Hours</h4>
                <div className="project-list">
                  <div
                    className={`project-item ${selectedProject === "All" ? "active" : ""}`}
                    onClick={() => setSelectedProject("All")}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="project-name">All Projects</span>
                    <span className="project-hours">({summary.total_hours})</span>
                  </div>
                  {projectHours.map((proj, idx) => (
                    <div
                      key={idx}
                      className={`project-item ${selectedProject === proj.project_name ? "active" : ""}`}
                      onClick={() => setSelectedProject(proj.project_name)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="project-name">{proj.project_name}</span>
                      <span className="project-hours">({proj.hours})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Tickets */}
            <div className="detail-right-col">
              <div className="tickets-container">
                <div className="tickets-header-controls">
                  <div className="status-tabs">
                    <button
                      className={`tab-btn ${activeTab === "All" ? "active" : ""}`}
                      onClick={() => setActiveTab("All")}
                    >
                      All ({tabCounts.All})
                    </button>
                    <button
                      className={`tab-btn ${activeTab === "Open" ? "active" : ""}`}
                      onClick={() => setActiveTab("Open")}
                    >
                      Open ({tabCounts.Open})
                    </button>
                    <button
                      className={`tab-btn ${activeTab === "In Progress" ? "active" : ""}`}
                      onClick={() => setActiveTab("In Progress")}
                    >
                      In Progress ({tabCounts["In Progress"]})
                    </button>
                    <button
                      className={`tab-btn ${activeTab === "Hold" ? "active" : ""}`}
                      onClick={() => setActiveTab("Hold")}
                    >
                      Hold ({tabCounts.Hold})
                    </button>
                  </div>

                  <div className="priority-filter-container">
                    <div
                      className="priority-selector"
                      onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                    >
                      <span className="priority-label">{priorityFilter}</span>
                      <ChevronDown size={16} />
                    </div>
                    {showPriorityDropdown && (
                      <div className="priority-dropdown-menu">
                        {["All Priority", "Low", "Medium", "High"].map(p => (
                          <div
                            key={p}
                            className={`priority-option ${priorityFilter === p ? "active" : ""}`}
                            onClick={() => {
                              setPriorityFilter(p);
                              setShowPriorityDropdown(false);
                            }}
                          >
                            {p}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="tickets-list">
                  {filteredTickets.map((ticket, idx) => (
                    <div
                      key={idx}
                      className="ticket-card"
                      onClick={() => navigate(`/ticket/view/${ticket.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="ticket-icon-wrapper">
                        <Layers size={20} />
                      </div>
                      <div className="ticket-details">
                        <div className="ticket-title-row">
                          <h5 className="ticket-title">{ticket.title}</h5>
                        </div>
                        <div className="ticket-meta-row">
                          <span>{ticket.project}</span>
                          <span className="stat-dot">•</span>
                          <span className="meta-status">{ticket.status}</span>
                          <span className="stat-dot">•</span>
                          <span className={`priority-badge priority-${(ticket.priority || 'Low').toLowerCase()}`}>
                            {ticket.priority || 'Low'}
                          </span>
                        </div>
                        <div className="ticket-estimation">
                          <Clock size={14} className="clock-icon" />
                          <span>Estimation : {ticket.estimation}</span>
                        </div>
                      </div>
                      {['open', 'in progress', 'hold'].includes(ticket.status?.toLowerCase()) && (
                        <button className="assign-btn" onClick={(e) => { e.stopPropagation(); setSelectedTicketToAssign(ticket); }}>Assign</button>
                      )}
                    </div>
                  ))}
                  {filteredTickets.length === 0 && (
                    <div className="no-tickets" style={{ padding: '40px', textAlign: 'center', color: '#8790A1' }}>No tickets found for this status.</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {selectedTicketToAssign && (
        <AssignTicketModal
          ticket={selectedTicketToAssign}
          onClose={() => setSelectedTicketToAssign(null)}
          onSuccess={() => {
            setRefreshTrigger(prev => prev + 1);
            if (onSuccess) onSuccess();
          }}
        />
      )}
    </div>
  );
};

export default WorkloadMemberDetail;
